<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { page } from '$app/stores';
  import { trackCtaClick } from '$lib/utils/analytics';

  const dispatch = createEventDispatcher();

  export let open = false;

  // Phones (≤768px) get a full-screen list. Tablets and narrow desktops
  // (769–1024px) get a compact panel under the hamburger; the nav bar still
  // shows Contact Us and Community Edition there, so the panel omits them.
  const products = [
    { href: '/products/azimuth', name: 'Azimuth', desc: 'AI-powered offensive security for smart contracts.' },
    { href: '/products/token-custody', name: 'Token Custody', desc: 'Continuous token risk analysis across 11M+ tokens.' },
    { href: '/products/agis', name: 'AGIS', desc: 'Earned autonomy for finance: prove agents, stop approving actions.' }
  ];
  const links = [
    { href: '/', name: 'Home' },
    { href: '/evmbench', name: 'Leaderboard' },
    { href: '/blog', name: 'Blog' }
  ];

  function handleLinkClick() {
    dispatch('close');
  }

  function handleKeydown(event: KeyboardEvent) {
    if (open && event.key === 'Escape') dispatch('close');
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<div class="mobile-menu-backdrop" class:active={open} on:click={handleLinkClick} aria-hidden="true"></div>

<div class="mobile-menu" class:active={open} aria-hidden={!open} inert={!open}>
  <div class="mobile-menu-inner">
    <p class="mobile-menu-label">Products</p>
    <div class="mobile-menu-products">
      {#each products as p}
        <a
          href={p.href}
          class="mobile-menu-link mobile-menu-product"
          class:active={$page.url.pathname === p.href}
          on:click={handleLinkClick}
        >
          <span class="mobile-menu-product-name">{p.name}</span>
          <span class="mobile-menu-product-desc">{p.desc}</span>
        </a>
      {/each}
    </div>

    <div class="mobile-menu-secondary">
      {#each links as l}
        <a
          href={l.href}
          class="mobile-menu-link"
          class:active={$page.url.pathname === l.href}
          on:click={handleLinkClick}
        >
          {l.name}
        </a>
      {/each}
      <a
        href="/#contact"
        class="mobile-menu-link mobile-menu-phone-only"
        on:click={() => { trackCtaClick('mobile_menu_contact', 'Contact Us', '/#contact'); handleLinkClick(); }}
      >
        Contact Us
      </a>
    </div>

    <div class="mobile-menu-cta mobile-menu-phone-only">
      <a href="https://app.testmachine.ai/" class="btn btn--primary btn--block" target="_blank" rel="noopener noreferrer" on:click={() => trackCtaClick('mobile_menu_launch_app', 'Community Edition', 'https://app.testmachine.ai/')}>
        Community Edition
      </a>
    </div>
  </div>
</div>

<style>
  /* ---------- Phones: full-screen list ---------- */
  .mobile-menu {
    position: fixed;
    top: var(--nav-h);
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--bg);
    z-index: 99;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
  }

  .mobile-menu.active {
    transform: translateX(0);
  }

  .mobile-menu-inner {
    display: flex;
    flex-direction: column;
    padding: 2rem;
    min-height: 100%;
  }

  .mobile-menu-link {
    display: block;
    font-family: var(--font-mono);
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--fg);
    padding: 1.5rem 0;
    border-bottom: 1px solid var(--border);
    transition: color var(--transition), background var(--transition), border-color var(--transition);
  }

  .mobile-menu-link:hover,
  .mobile-menu-link.active {
    color: var(--editorial-muted);
  }

  .mobile-menu-products,
  .mobile-menu-secondary {
    display: contents;
  }

  /* Phones keep the original single list: Home first, then products. */
  .mobile-menu-secondary .mobile-menu-link:first-child { order: -1; }
  .mobile-menu-label,
  .mobile-menu-product-desc,
  .mobile-menu-backdrop {
    display: none;
  }

  .mobile-menu-cta {
    margin-top: auto;
    padding-top: 2rem;
  }

  /* ---------- Tablets & narrow desktops: compact panel ---------- */
  @media (min-width: 769px) and (max-width: 1024px) {
    .mobile-menu-backdrop {
      display: block;
      position: fixed;
      inset: var(--nav-h) 0 0 0;
      z-index: 98;
      background: rgba(17, 19, 20, 0.28);
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;
    }

    .mobile-menu-backdrop.active {
      opacity: 1;
      pointer-events: auto;
    }

    .mobile-menu {
      left: auto;
      right: max(24px, calc((100vw - var(--max-w)) / 2));
      bottom: auto;
      top: calc(var(--nav-h) + 8px);
      width: 380px;
      max-height: calc(100vh - var(--nav-h) - 24px);
      border: 1px solid var(--border);
      box-shadow: 0 24px 60px -20px rgba(17, 19, 20, 0.35);
      transform: translateY(-8px);
      opacity: 0;
      visibility: hidden;
      transition: transform 0.2s ease, opacity 0.2s ease, visibility 0s linear 0.2s;
    }

    .mobile-menu.active {
      transform: translateY(0);
      opacity: 1;
      visibility: visible;
      transition: transform 0.2s ease, opacity 0.2s ease;
    }

    .mobile-menu-inner {
      padding: 12px;
      min-height: 0;
      text-align: left;
    }

    .mobile-menu-label {
      display: block;
      padding: 8px 12px 6px;
      font-family: var(--font-mono);
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--editorial-muted);
    }

    .mobile-menu-products {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .mobile-menu-product {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 12px;
      border: 1px solid transparent;
    }

    .mobile-menu-product:hover,
    .mobile-menu-product.active {
      background: var(--card-bg);
      border-color: var(--editorial);
      color: var(--fg);
    }

    .mobile-menu-product-name {
      font-family: var(--font-display);
      font-size: 1rem;
      font-weight: 700;
    }

    .mobile-menu-product.active .mobile-menu-product-name {
      color: var(--editorial-muted);
    }

    .mobile-menu-product-desc {
      display: block;
      font-family: var(--font-mono);
      font-size: 0.72rem;
      font-weight: 400;
      line-height: 1.45;
      color: var(--fg-muted);
    }

    .mobile-menu-secondary {
      display: flex;
      gap: 4px;
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid var(--border-subtle);
    }

    .mobile-menu-secondary .mobile-menu-link {
      flex: 1;
      text-align: center;
      font-size: 0.8rem;
      padding: 10px 8px;
      border: 1px solid transparent;
    }

    .mobile-menu-secondary .mobile-menu-link:hover,
    .mobile-menu-secondary .mobile-menu-link.active {
      background: var(--card-bg);
      border-color: var(--border);
    }

    .mobile-menu-phone-only {
      display: none;
    }
  }

  /* Match Navigation.svelte, which shows the hamburger up to 1024px. */
  @media (min-width: 1025px) {
    .mobile-menu,
    .mobile-menu-backdrop {
      display: none;
    }
  }
</style>
